import { useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from '@mui/material/styles';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  headerColor = "info",
  headerTextColor = "white",
}) {
  const theme = useTheme();

  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];

  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const { page: controlledPageIndex, onPageChange, rowsPerPage: controlledPageSize, onRowsPerPageChange, count } = pagination;

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: controlledPageIndex || 0, pageSize: controlledPageSize || defaultValue },
      manualPagination: true,
      pageCount: Math.ceil(count / controlledPageSize),
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    state: { globalFilter, pageIndex, pageSize },
    setGlobalFilter,
    setPageSize,
  } = tableInstance;

  useEffect(() => {
    if (controlledPageSize !== pageSize) {
      setPageSize(controlledPageSize);
    }
  }, [controlledPageSize, pageSize, setPageSize]);

  const handleEntriesPerPageChange = (event, newValue) => {
    const newSize = parseInt(newValue, 10);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(event, newSize);
    } else {
      setPageSize(newSize);
    }
  };

  const renderPagination = useMemo(() => {
    const totalPages = Math.ceil(count / controlledPageSize);
    const pages = Array.from({ length: totalPages }, (_, i) => i);

    return pages.map((option) => (
      <MDPagination
        item
        key={option}
        onClick={(e) => onPageChange(e, option)}
        active={controlledPageIndex === option}
      >
        {option + 1}
      </MDPagination>
    ));
  }, [count, controlledPageSize, controlledPageIndex, onPageChange]);


  const handleInputPagination = ({ target: { value } }) => {
    const newPage = Number(value) - 1;
    const totalPages = Math.ceil(count / controlledPageSize);
    if (newPage >= 0 && newPage < totalPages && onPageChange) {
      onPageChange(null, newPage);
    } else if (onPageChange) {
      onPageChange(null, 0);
    }
  };

  const entriesStart = controlledPageIndex * controlledPageSize + 1;
  let entriesEnd = Math.min((controlledPageIndex + 1) * controlledPageSize, count);


  const [search, setSearch] = useState(globalFilter);

  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  const resolvedHeaderBackgroundColor =
    headerColor && theme.palette[headerColor]
      ? theme.palette[headerColor].main
      : headerColor;

  return (
    <TableContainer
      sx={{
        boxShadow: "none",
        borderRadius: "10px",
        // overflow: "hidden", // Removed to ensure overflowX/Y works correctly without clipping
        border: `1px solid ${theme.palette.grey[300]}`,


        overflowX: 'auto',   // Enable horizontal scrolling
      }}
    >
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={controlledPageSize.toString()}
                options={entries}
                onChange={handleEntriesPerPageChange}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="black">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      <Table {...getTableProps()}>
        <MDBox component="thead"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: resolvedHeaderBackgroundColor,
          }}
        >
          {headerGroups.map((headerGroup, key) => (
            <TableRow
              key={key}
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map((column, idx) => (
                <DataTableHeadCell
                  key={idx}
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                  textColor={headerTextColor}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow key={key} {...row.getRowProps()}>
                {row.cells.map((cell, idx) => (
                  <DataTableBodyCell
                    key={idx}
                    noBorder={noEndBorder && page.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && Math.ceil(count / controlledPageSize) === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="black" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {count} entries
            </MDTypography>
          </MDBox>
        )}
        {Math.ceil(count / controlledPageSize) > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {controlledPageIndex > 0 && (
              <MDPagination item onClick={(e) => onPageChange(e, controlledPageIndex - 1)}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{ type: "number", min: 1, max: Math.ceil(count / controlledPageSize) }}
                  value={controlledPageIndex + 1}
                  onChange={handleInputPagination}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {controlledPageIndex < Math.ceil(count / controlledPageSize) - 1 && (
              <MDPagination item onClick={(e) => onPageChange(e, controlledPageIndex + 1)}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  isSorted: true,
  noEndBorder: false,
  headerColor: "info",
  headerTextColor: "white",
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
    page: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
    count: PropTypes.number.isRequired,
  }).isRequired,
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  headerColor: PropTypes.oneOfType([
    PropTypes.oneOf([
      "primary", "secondary", "info", "success", "warning", "error", "dark", "light",
    ]),
    PropTypes.string,
  ]),
  headerTextColor: PropTypes.string,
};

export default DataTable;